import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { connectSocket } from '../../services/realtime/socket';

// Drives the student dashboard's live-poll card. Initial state comes from a
// REST fetch (mirrors the attendance active-today pattern); after that,
// Socket.IO keeps it live via the poll:course/poll:elective rooms the
// connection auto-joins on connect (see server/realtime/socketServer.js) -
// no polling interval, no separate SSE connection. Once a poll is known,
// this also joins that specific poll session's room (same as the Join a
// Poll page) so live result tallies stay current after answering.
export default function useActivePoll() {
  const { user } = useAuth();
  const userId = user?.id;
  const userRole = user?.role;
  const [poll, setPoll] = useState(null);
  const [myAnswerCount, setMyAnswerCount] = useState(0);
  const [optionTallies, setOptionTallies] = useState([]);
  const [ratingStats, setRatingStats] = useState(null);
  const [wordCloudData, setWordCloudData] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const joinedPollSessionIdRef = useRef(null);
  const pollCodeRef = useRef(null);

  const loadResults = useCallback(async (code) => {
    try {
      const data = await apiService.getPollState(code);
      setOptionTallies(data.optionTallies || []);
      setRatingStats(data.ratingStats || null);
      setWordCloudData(data.wordCloud || []);
      setAnswers(Array.isArray(data.answers) ? data.answers : []);
    } catch (e) {
      // Results are a bonus on top of the answer form - fail quietly.
    }
  }, []);

  const load = useCallback(async () => {
    if (!userId || userRole !== 'student') {
      setPoll(null);
      setInitialLoadDone(true);
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.getActivePollForStudent();
      if (response.success) {
        setPoll(response.poll || null);
        // Server is the source of truth for how many times this student has
        // already answered - survives page reloads, unlike a local flag.
        setMyAnswerCount(response.poll?.myAnswerCount || 0);
        if (response.poll?.code) loadResults(response.poll.code);
      }
    } catch (e) {
      // Non-critical dashboard widget - fail quietly, next poll event or
      // remount will pick the right state back up.
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [userId, userRole, loadResults]);

  useEffect(() => {
    if (!userId || userRole !== 'student') {
      setPoll(null);
      return undefined;
    }

    load();

    const socket = connectSocket();

    const handleActivated = (payload) => {
      setPoll(payload);
      setMyAnswerCount(0);
      setOptionTallies([]);
      setRatingStats(null);
      setWordCloudData([]);
      setAnswers([]);
      setError('');
      if (payload.pollSessionId && joinedPollSessionIdRef.current !== payload.pollSessionId) {
        socket.emit('poll:join', { pollSessionId: payload.pollSessionId });
        joinedPollSessionIdRef.current = payload.pollSessionId;
      }
    };
    const handleClosed = (payload) => {
      setPoll((prev) => (prev && prev.pollSessionId === payload.pollSessionId ? null : prev));
    };
    const handleResultsUpdate = (data) => {
      if (data.pollSessionId === joinedPollSessionIdRef.current && pollCodeRef.current) {
        loadResults(pollCodeRef.current);
      }
    };

    socket.on('poll:question:activated', handleActivated);
    socket.on('poll:closed', handleClosed);
    socket.on('poll:answer:new', handleResultsUpdate);
    socket.on('poll:vote:update', handleResultsUpdate);

    return () => {
      socket.off('poll:question:activated', handleActivated);
      socket.off('poll:closed', handleClosed);
      socket.off('poll:answer:new', handleResultsUpdate);
      socket.off('poll:vote:update', handleResultsUpdate);
      if (joinedPollSessionIdRef.current) {
        socket.emit('poll:leave', { pollSessionId: joinedPollSessionIdRef.current });
        joinedPollSessionIdRef.current = null;
      }
    };
  }, [userId, userRole, load, loadResults]);

  // Keep the ref in sync so the socket handlers above (registered once, not
  // re-bound on every poll update) always reload the right poll's results.
  useEffect(() => {
    pollCodeRef.current = poll?.code || null;
  }, [poll?.code]);

  // The initial REST load (above) doesn't know the poll session id until the
  // response lands, so join that room here once it's known.
  useEffect(() => {
    if (!poll?.pollSessionId || joinedPollSessionIdRef.current === poll.pollSessionId) return;
    const socket = connectSocket();
    socket.emit('poll:join', { pollSessionId: poll.pollSessionId });
    joinedPollSessionIdRef.current = poll.pollSessionId;
  }, [poll?.pollSessionId]);

  const submitAnswer = useCallback(async ({ answerText = '', optionId = null, ratingValue = null } = {}) => {
    if (!poll) return;
    const maxAnswers = poll.question.maxAnswersPerUser || 1;
    if (myAnswerCount >= maxAnswers) return;
    const trimmed = answerText.trim();
    if (!optionId && !ratingValue && !trimmed) return;
    try {
      setSubmitting(true);
      setError('');
      const response = await apiService.submitPollAnswer(
        poll.code, poll.question.id, poll.question.groupLabel || null, trimmed, optionId, ratingValue
      );
      if (response?.success === false) {
        setError(response.error || 'Failed to submit answer');
        return;
      }
      setMyAnswerCount((c) => c + 1);
      loadResults(poll.code);
    } catch (e) {
      setError(e.message || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  }, [poll, myAnswerCount, loadResults]);

  const voteOnIdea = useCallback(async (answerId) => {
    try {
      await apiService.votePollAnswer(answerId);
      if (poll?.code) loadResults(poll.code);
    } catch (e) {
      setError(e.message || 'Failed to vote');
    }
  }, [poll, loadResults]);

  const maxAnswers = poll?.question?.maxAnswersPerUser || 1;
  const answered = myAnswerCount >= maxAnswers;

  return {
    poll, loading, initialLoadDone, answered, myAnswerCount, maxAnswers, submitting, error, submitAnswer,
    optionTallies, ratingStats, wordCloudData, answers, voteOnIdea,
  };
}
